import React from 'react';
import { Box, Card, CardContent, Typography, Button, Stack, Chip, Fade } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import HistoryIcon from '@mui/icons-material/History';
import { modernTheme, formatHours, iconBoxSx, formatMoney, monthLabel } from './theme';

const fmt = (v) => new Intl.NumberFormat('vi-VN').format(v || 0) + 'đ';

const IncomeCard = ({
  userSummary, baseSalary = 0, commissionData, leaderData, userRole,
  pendingAdvance, onOpenAdvancePayment, onOpenAdvanceHistory
}) => {
  const isLeader = userRole === 'leader_live';
  const liveEarnings = userSummary?.currentPeriod?.earnings || 0;
  const hourlyRate = userSummary?.hourlyRate || 0;
  const liveHours = userSummary?.currentPeriod?.hours || 0;
  const commissionAmount = commissionData?.commissionAmount || 0;
  const commissionPercent = commissionData?.commissionPercent || 0;
  const gmv = commissionData?.totalGMV || 0;
  const hasCommission = commissionAmount > 0 || commissionPercent > 0;

  // Leader-specific data
  const team = leaderData?.team;
  const leaderBaseSalary = isLeader ? (leaderData?.baseSalary || baseSalary) : baseSalary;
  const teamCommission = team?.teamCommission || 0;
  const growthBonus = team?.growthBonus || 0;
  const tiktokKpiBonus = team?.tiktokKpiBonus || 0;
  const totalLeaderBonus = team?.totalLeaderBonus || 0;
  // Từ 10/2026: so với kỷ lục GMV team 6 tháng gần nhất thay vì tháng trước
  const isPeak = team?.growthBaseline === 'peak';

  const totalConfirmed = leaderBaseSalary + liveEarnings;
  const totalEstimated = isLeader
    ? leaderBaseSalary + liveEarnings + commissionAmount + totalLeaderBonus
    : totalConfirmed + commissionAmount;
  const hasBonus = isLeader ? (totalLeaderBonus > 0 || hasCommission) : hasCommission;

  return (
    <Fade in timeout={600}>
      <Card sx={{
        borderRadius: 3,
        border: `1px solid ${modernTheme.colors.gray[200]}`,
        boxShadow: modernTheme.shadows.md,
        background: modernTheme.gradients.card,
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: modernTheme.shadows.lg }
      }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <Box sx={iconBoxSx(modernTheme.gradients.primary, 44)}>
              <AccountBalanceWalletIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box sx={{ ml: 1.5, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ color: modernTheme.colors.gray[900], fontWeight: 700, lineHeight: 1.2 }}>
                  Thu nhập kỳ này
                </Typography>
                {isLeader && (
                  <Chip label="LEAD" size="small" sx={{
                    height: 20, fontSize: '0.6rem', fontWeight: 700,
                    bgcolor: '#7c3aed', color: 'white',
                  }} />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500], fontSize: '0.7rem' }}>
                {userSummary ? `${new Date(userSummary.currentPeriod.startDate).toLocaleDateString('vi-VN')} - ${new Date(userSummary.currentPeriod.endDate).toLocaleDateString('vi-VN')}` : ''}
              </Typography>
            </Box>
          </Box>

          {/* Total Estimated */}
          <Box sx={{
            p: 2, borderRadius: 2.5, mb: 2,
            bgcolor: `${modernTheme.colors.primary}08`,
            border: `1.5px solid ${modernTheme.colors.primary}20`,
          }}>
            <Typography variant="caption" sx={{
              color: modernTheme.colors.primary, fontWeight: 700,
              fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px',
              display: 'block', mb: 0.5,
            }}>
              {hasBonus ? 'Tổng ước tính (gồm hoa hồng & bonus)' : 'Tổng thu nhập kỳ này'}
            </Typography>
            <Typography variant="h4" sx={{
              color: modernTheme.colors.primary,
              fontWeight: 800, lineHeight: 1.2,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
            }}>
              {fmt(hasBonus ? totalEstimated : totalConfirmed)}
            </Typography>
          </Box>

          {/* Breakdown */}
          <Box sx={{
            p: 1.5, borderRadius: 2,
            bgcolor: modernTheme.colors.gray[50],
            border: `1px solid ${modernTheme.colors.gray[200]}`,
            mb: 2,
          }}>
            <Typography variant="caption" sx={{
              color: modernTheme.colors.gray[500], fontWeight: 700,
              fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px',
              display: 'block', mb: 1,
            }}>
              Chi tiết
            </Typography>

            <Stack spacing={0.75}>
              {/* Luong co ban */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" sx={{ color: modernTheme.colors.gray[600], fontSize: '0.78rem' }}>
                  {isLeader ? 'Lương cứng/tháng' : 'Lương cơ bản'}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', fontSize: '0.78rem' }}>
                  {fmt(leaderBaseSalary)}
                </Typography>
              </Stack>

              {/* Gio live */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" sx={{ color: modernTheme.colors.gray[600], fontSize: '0.78rem' }}>
                  Giờ live ({formatHours(liveHours)}h x {new Intl.NumberFormat('vi-VN').format(hourlyRate)}đ)
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#065f46', fontSize: '0.78rem' }}>
                  {fmt(liveEarnings)}
                </Typography>
              </Stack>

              {/* === LEADER BONUSES === */}
              {isLeader && team && (
                <>
                  {/* Divider */}
                  <Box sx={{ borderTop: '1px dashed #d1d5db', my: 0.5 }} />

                  <Typography variant="caption" sx={{
                    color: '#7c3aed', fontWeight: 700, fontSize: '0.6rem',
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>
                    Bonus Leader
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: modernTheme.colors.gray[400], fontSize: '0.65rem',
                    fontStyle: 'italic', lineHeight: 1.3, display: 'block', mb: 0.25,
                  }}>
                    Tính trên tổng GMV các thành viên team, không bao gồm GMV cá nhân của Lead
                  </Typography>

                  {/* Hoa hong team GMV */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ color: modernTheme.colors.gray[600], fontSize: '0.78rem' }}>
                      HH Team GMV ({team.teamCommissionPercent}%)
                      <Typography component="span" sx={{ color: modernTheme.colors.gray[400], fontSize: '0.68rem', ml: 0.5 }}>
                        (GMV {new Intl.NumberFormat('vi-VN').format(team.teamTotalGMV)}đ)
                      </Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed', fontSize: '0.78rem' }}>
                      {fmt(teamCommission)}
                    </Typography>
                  </Stack>

                  {/* Thuong vuot target */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ color: modernTheme.colors.gray[600], fontSize: '0.78rem' }}>
                      Thưởng vượt target ({team.growthBonusPercent}%)
                      {growthBonus > 0 && (
                        <Typography component="span" sx={{ color: '#059669', fontSize: '0.68rem', ml: 0.5 }}>
                          (+{new Intl.NumberFormat('vi-VN').format(team.growthAmount)}đ)
                        </Typography>
                      )}
                      {isPeak && team.baselineTeamMonth && (
                        <Typography component="span" sx={{ display: 'block', color: modernTheme.colors.gray[400], fontSize: '0.66rem' }}>
                          so với kỷ lục 6 tháng: {monthLabel(team.baselineTeamMonth)} {formatMoney(team.baselineTeamGMV)}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontWeight: 700, fontSize: '0.78rem',
                      color: growthBonus > 0 ? '#059669' : modernTheme.colors.gray[400],
                    }}>
                      {growthBonus > 0 ? fmt(growthBonus) : 'Chưa đạt'}
                    </Typography>
                  </Stack>

                  {/* KPI TikTok */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ color: modernTheme.colors.gray[600], fontSize: '0.78rem' }}>
                      KPI TikTok ({team.tiktokKpiBonusPercent}%)
                      <Typography component="span" sx={{
                        color: (isPeak ? tiktokKpiBonus > 0 : team.tiktokGrowthPercent >= team.tiktokGrowthThreshold) ? '#059669' : modernTheme.colors.gray[400],
                        fontSize: '0.68rem', ml: 0.5,
                      }}>
                        {isPeak && team.baselineTiktokMonth
                          ? `(${team.tiktokGrowthPercent >= 0 ? '+' : ''}${team.tiktokGrowthPercent}% so kỷ lục ${monthLabel(team.baselineTiktokMonth)})`
                          : `(tăng ${team.tiktokGrowthPercent}%)`}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontWeight: 700, fontSize: '0.78rem',
                      color: tiktokKpiBonus > 0 ? '#059669' : modernTheme.colors.gray[400],
                    }}>
                      {tiktokKpiBonus > 0 ? fmt(tiktokKpiBonus) : 'Chưa đạt'}
                    </Typography>
                  </Stack>
                </>
              )}

              {/* === PERSONAL COMMISSION (both leader and normal) === */}
              {hasCommission && (
                <>
                  {isLeader && (
                    <>
                      <Box sx={{ borderTop: '1px dashed #d1d5db', my: 0.5 }} />
                      <Typography variant="caption" sx={{
                        color: '#92400e', fontWeight: 700, fontSize: '0.6rem',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                      }}>
                        Hoa hồng cá nhân
                      </Typography>
                      <Typography variant="caption" sx={{
                        color: modernTheme.colors.gray[400], fontSize: '0.65rem',
                        fontStyle: 'italic', lineHeight: 1.3, display: 'block', mb: 0.25,
                      }}>
                        Tính khi Lead trực tiếp live (ngày sale hoặc hỗ trợ), dựa trên GMV cá nhân
                      </Typography>
                    </>
                  )}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ color: modernTheme.colors.gray[600], fontSize: '0.78rem' }}>
                      {isLeader ? 'HH cá nhân' : 'Hoa hồng'} {commissionData?.isTimeBased ? 'theo giờ' : ''} ~{commissionPercent}%
                      {gmv > 0 && (
                        <Typography component="span" sx={{ color: modernTheme.colors.gray[400], fontSize: '0.68rem', ml: 0.5 }}>
                          (GMV {new Intl.NumberFormat('vi-VN').format(gmv)}đ)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', fontSize: '0.78rem' }}>
                      {fmt(commissionAmount)}
                    </Typography>
                  </Stack>
                  {/* Breakdown per revenue session */}
                  {commissionData?.isTimeBased && commissionData?.revenueBreakdown?.length > 0 && (
                    <Box sx={{ ml: 1.5, mt: 0.5, pl: 1, borderLeft: '2px solid #fde68a' }}>
                      {commissionData.revenueBreakdown.map((rb, idx) => (
                        <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                          <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500], fontSize: '0.68rem' }}>
                            Phiên {new Intl.NumberFormat('vi-VN').format(rb.totalSales)}đ
                            {rb.isTimeBased && rb.breakdown?.length > 0 && (
                              <Typography component="span" sx={{ color: '#d97706', fontSize: '0.62rem', ml: 0.5 }}>
                                ({rb.breakdown.map(b => `${b.startTime}-${b.endTime}: ${b.rate}%`).join(', ')})
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.68rem' }}>
                            {rb.effectiveRate}% = {fmt(rb.commissionAmount)}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Box>

          {/* Confirmed total */}
          {hasBonus && (
            <Box sx={{
              p: 1.25, borderRadius: 2, mb: 2,
              bgcolor: '#ecfdf5',
              border: '1px solid #a7f3d0',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#065f46', fontWeight: 600, fontSize: '0.7rem' }}>
                    Thu nhập chắc chắn
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.6rem', display: 'block' }}>
                    Chưa tính hoa hồng & bonus
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 800 }}>
                  {fmt(totalConfirmed)}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Action Buttons */}
          <Stack spacing={1}>
            <Button
              variant="contained" fullWidth
              startIcon={<CreditScoreIcon />}
              onClick={onOpenAdvancePayment}
              disabled={pendingAdvance !== null}
              sx={{
                background: modernTheme.gradients.primary,
                color: 'white', py: 1.25, borderRadius: 2,
                textTransform: 'none', fontWeight: 600,
                boxShadow: modernTheme.shadows.sm,
                '&:hover': { background: modernTheme.gradients.primary, boxShadow: modernTheme.shadows.md },
                '&:disabled': { bgcolor: modernTheme.colors.gray[300], color: modernTheme.colors.gray[500] },
              }}
            >
              {pendingAdvance ? 'Đang chờ duyệt' : 'Ứng tiền ngay'}
            </Button>
            <Button
              variant="outlined" fullWidth
              startIcon={<HistoryIcon />}
              onClick={onOpenAdvanceHistory}
              sx={{
                borderColor: modernTheme.colors.gray[300],
                color: modernTheme.colors.gray[700],
                py: 1.25, borderRadius: 2,
                textTransform: 'none', fontWeight: 600,
                '&:hover': { borderColor: modernTheme.colors.primary, color: modernTheme.colors.primary },
              }}
            >
              Lịch sử ứng tiền
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default IncomeCard;
